import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ChevronLeft, ChevronRight, RefreshCw, Sparkles, Layers, AlertCircle, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MD } from './Markdown';
import { streamDifyChat, isDifyConfigured } from '../../lib/difyClient';
import { canConsume, consume, getCurrentTier } from '../../lib/quota';
import { toast } from 'sonner';

const DEMO_CARDS = [
  { front: 'श्रृंगार रस', back: 'स्थायी भाव: रति। प्रेम और सौंदर्य का वर्णन।' },
  { front: 'छायावाद', back: 'प्रसाद, पंत, निराला, महादेवी — आत्मानुभूति प्रधान काव्य।' },
  { front: 'दोहा छंद', back: 'अर्धसम मात्रिक छंद, कुल 48 मात्राएँ (13+11)।' },
  { front: 'रूपक अलंकार', back: 'उपमेय में उपमान का आरोप — "मुख चन्द्र है"।' },
  { front: 'भक्तिकाल', back: 'सगुण (राम/कृष्ण) और निर्गुण (ज्ञान/प्रेम) धाराएँ।' },
];

const CHIPS = ['Ras', 'Alankar', 'Chhand', 'Bhaktikaal', 'Chhayavad', 'Vyakaran', 'Bhartiya Kavyashastra', 'Godan', 'Kamayani', 'Premchand', 'Tulsidas', 'Kabir', 'Aacharya & Sampraday', 'Aadhunik Kaal', 'Riti Kaal'];

// Parse cards from Dify markdown response.
// Expected formats we handle:
// 1) "**Card 1**\nFront: ...\nBack: ..."
// 2) Numbered: "1. **front**\n  - back"
// 3) Each "###" heading is a card
function parseCards(text) {
  if (!text) return [];
  const cards = [];

  // Pattern: "Front: X\nBack: Y" (works for **Card N**\nFront:..\nBack:..)
  const re1 = /(?:\*\*)?(?:Front|प्रश्न|प्रश्न\s*\(Front\)|शीर्षक)[^:\n]*:\s*([^\n]+)\n[\s\S]*?(?:\*\*)?(?:Back|उत्तर|व्याख्या|उत्तर\s*\(Back\)|जवाब)[^:\n]*:\s*([\s\S]*?)(?=\n\s*(?:\*\*)?(?:Card|कार्ड|प्रश्न|Front|शीर्षक)[^:\n]*[:\d]|\n\s*---|\n\s*\d+[\.\)]\s|$)/gi;
  let m;
  while ((m = re1.exec(text)) !== null) {
    let front = m[1].trim().replace(/^\*+|\*+$/g, '').replace(/^\[|\]$/g, '').trim();
    let back = m[2].trim().replace(/^\*+|\*+$/g, '').replace(/\n\s*\*\*Card\s*\d+\*\*[\s\S]*$/i, '').trim();
    if (front && back) cards.push({ front, back });
  }
  if (cards.length >= 2) return cards;

  // Pattern: numbered list "1. **Topic** — explanation"
  const blocks = text.split(/\n(?=\s*\d+[\.\)]\s)/);
  if (blocks.length >= 3) {
    blocks.forEach(b => {
      const fm = b.match(/^\s*\d+[\.\)]\s*\*?\*?([^*\n]+?)\*?\*?(?:\s*[—\-:]\s*|\n)([\s\S]+)/);
      if (fm) cards.push({ front: fm[1].trim(), back: fm[2].trim() });
    });
    if (cards.length >= 2) return cards;
  }

  // Pattern: ## headings
  const heads = text.split(/\n(?=#{2,3}\s)/);
  if (heads.length >= 3) {
    heads.forEach(h => {
      const fm = h.match(/^#{2,3}\s*(.+)\n([\s\S]+)/);
      if (fm) cards.push({ front: fm[1].trim(), back: fm[2].trim() });
    });
    if (cards.length >= 2) return cards;
  }

  return cards;
}

export const FlashcardsPage = ({ onBack }) => {
  const [topic, setTopic] = useState('');
  const [pending, setPending] = useState('');
  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [started, setStarted] = useState(false);
  const [rawResponse, setRawResponse] = useState(''); // fallback display
  const abortRef = useRef(null);

  const quizKey = process.env.REACT_APP_DIFY_QUIZ_API_KEY;
  // Flashcards use the general chat key — it's more flexible than the quiz-only tutor
  const flashKey = process.env.REACT_APP_DIFY_API_KEY || quizKey;
  const configured = isDifyConfigured(flashKey);

  const generate = async (t) => {
    if (!canConsume('flashcards')) {
      const tier = getCurrentTier();
      toast.error('Daily flashcard limit reached', {
        description: tier.id === 'monthly' ? 'Unexpected — retry.' : `${tier.label} allows ${tier.flashcards}/day. Upgrade.`,
      });
      return;
    }
    setStarted(true);
    setLoading(true); setError(null); setIdx(0); setFlipped(false);
    setTopic(t);
    setCards([]);
    setRawResponse('');
    consume('flashcards');

    if (!configured) {
      setCards(DEMO_CARDS);
      setLoading(false);
      return;
    }

    const query = `कृपया "${t}" विषय पर UGC NET / TGT / PGT स्तर के 10 फ्लैशकार्ड (flashcards) बनाएँ। प्रत्येक कार्ड को इस format में दें:

**Card 1**
Front: [शीर्षक / प्रश्न / concept]
Back: [विस्तृत व्याख्या markdown में — अगर tulna/मिलान हो to markdown table use करें]

**Card 2**
Front: ...
Back: ...

कुल 10 cards दीजिए। हर कार्ड पर एक concept hovera।`;

    const ac = new AbortController();
    abortRef.current = ac;
    let answer = '';
    try {
      await streamDifyChat({
        apiKey: quizKey,
        query,
        user: 'brahmastra-fc',
        signal: ac.signal,
        onEvent: (ev) => {
          if (ev.event === 'message' || ev.event === 'agent_message') {
            answer += ev.answer || '';
          } else if (ev.event === 'error') {
            throw new Error(ev.message || 'Dify error');
          }
        },
      });
      setRawResponse(answer);
      const parsed = parseCards(answer);
      if (parsed.length >= 2) {
        setCards(parsed.slice(0, 12));
      } else {
        // Fallback: show whole response as ONE single card
        setCards([{ front: t, back: answer || 'No content' }]);
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      setError(e.message);
      setCards(DEMO_CARDS);
    } finally {
      setLoading(false);
    }
  };

  const c = cards[idx];

  return (
    <main className="flex-1 min-w-0 min-h-0 flex flex-col bg-app overflow-y-auto thin-scroll" data-testid="flashcards-page">
      <div className="sticky top-0 z-10 border-b border-border bg-card/60 glass">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <button onClick={onBack} data-testid="fc-back-btn" className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground hover:text-foreground transition">
            <ArrowLeft className="w-4 h-4" /> Back to chat
          </button>
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-bblue" />
            <span className="text-[13px] font-semibold">Flashcards</span>
          </div>
          {started && cards.length > 0 ? (
            <div className="text-[12px] text-muted-foreground">{idx + 1} / {cards.length}</div>
          ) : <span className="w-16" />}
        </div>
      </div>

      <div className="max-w-full mx-auto w-full px-3 sm:px-6 py-6 sm:py-10 flex-1">
        {!started ? (
          <div className="anim-fade-up">
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Choose a topic for flashcards</h1>
              <p className="mt-1.5 text-[13px] text-muted-foreground">AI-generated cards · tap to flip · tables & comparisons supported</p>
            </div>
            <div className="mt-6 sm:mt-8 max-w-xl mx-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={pending}
                  onChange={(e) => setPending(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && pending.trim()) generate(pending.trim()); }}
                  placeholder="Type any topic — e.g., 'Bhartiya Kavyashastra', 'Acharya & Sampraday'..."
                  className="pl-9 h-11 bg-card border-border focus-visible:ring-bblue/30"
                  data-testid="fc-topic-input"
                />
              </div>
              <div className="mt-3 text-[10.5px] text-muted-foreground text-center">or pick a quick topic ↓</div>
              <div className="mt-3 flex flex-nowrap sm:flex-wrap gap-2 overflow-x-auto pb-1 px-1 -mx-1">
                {CHIPS.map((ch) => (
                  <button
                    key={ch}
                    onClick={() => generate(ch)}
                    data-testid={`fc-chip-${ch.replace(/\s+/g, '-')}`}
                    className="flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] border border-border bg-card hover:border-bblue/40 hover:bg-secondary/60 transition"
                  >
                    {ch}
                  </button>
                ))}
              </div>
              <div className="mt-6 flex justify-center">
                <Button
                  onClick={() => generate(pending.trim() || 'Ras')}
                  className="bg-bblue text-[#001a26] hover:brightness-110 px-8 h-11"
                  data-testid="fc-start-btn"
                >
                  Generate Flashcards
                </Button>
              </div>
            </div>
          </div>
        ) : loading ? (
          <div className="py-20 text-center text-muted-foreground" data-testid="fc-loading">
            <span className="dot-pulse"><span /><span /><span /></span>
            <div className="mt-3 text-[13px]">AI flashcards bana raha hai...</div>
          </div>
        ) : c ? (
          <div className="anim-fade-up">
            <div className="text-center mb-4">
              <div className="text-[11px] text-muted-foreground">Topic</div>
              <div className="text-[14px] font-medium text-bblue">{topic}</div>
            </div>
            <div className="fc-scene">
              <div
                className={`fc-card cursor-pointer ${flipped ? 'flipped' : ''}`}
                onClick={() => setFlipped(f => !f)}
                data-testid="fc-card"
                style={{ maxWidth: '100%' }}
              >
                <div className="fc-face bg-gradient-to-br from-secondary/80 to-card border border-border">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Front · Tap to flip</span>
                  <h3 className="mt-3 text-2xl sm:text-3xl font-bold devanagari text-foreground text-center px-4">{c.front}</h3>
                  <Sparkles className="absolute top-4 right-4 w-4 h-4 text-mint/60" />
                </div>
                <div className="fc-face fc-back bg-gradient-to-br from-bblue/10 to-card border border-bblue/30" style={{ padding: 0 }}>
                  <div className="w-full h-full overflow-auto thin-scroll p-5 flex flex-col">
                    <span className="text-[10px] uppercase tracking-widest text-bblue">Back · Tap to flip</span>
                    <div className="mt-3 text-[13.5px] leading-relaxed devanagari text-foreground/90 w-full text-left">
                      <MD accent="bblue">{c.back}</MD>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-5">
              <Button variant="outline" size="sm" onClick={() => { setFlipped(false); setTimeout(() => setIdx(i => Math.max(i - 1, 0)), 50); }} disabled={idx === 0} data-testid="fc-prev">
                <ChevronLeft className="w-4 h-4" /> Prev
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { setStarted(false); setPending(''); }} data-testid="fc-restart">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> New Topic
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setFlipped(false); setTimeout(() => setIdx(i => Math.min(i + 1, cards.length - 1)), 50); }} disabled={idx === cards.length - 1} data-testid="fc-next">
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            {error && (
              <div className="text-[10.5px] text-amber-400 flex items-center gap-1.5 mt-3 justify-center">
                <AlertCircle className="w-3 h-3" /> Using demo ({error})
              </div>
            )}
          </div>
        ) : null}
      </div>
    </main>
  );
};

export default FlashcardsPage;
